/**
 * NULL 1:1 REBUILD — banner slideshow (§5.5)
 *
 * - translateX track, one slide per frame, wraps around
 * - autoplay on a configurable interval, paused on hover, focus and
 *   whenever the tab is hidden
 * - touch/pointer swipe with live drag feedback
 * - dots + optional arrows, both keyboard accessible
 * - prefers-reduced-motion: no autoplay, no slide transition
 *
 * Deliberately timer-driven, never requestAnimationFrame — see the note
 * in null-shop-by-bike.js. A hero that silently stops advancing in a
 * throttled webview is worse than one that jumps.
 */
class NullSlideshow extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.vbs-viewport');
    this.track = this.querySelector('.vbs-track');
    this.slides = Array.from(this.querySelectorAll('.vbs-slide'));
    this.dots = Array.from(this.querySelectorAll('.vbs-dot'));
    this.prevBtn = this.querySelector('[data-vbs-prev]');
    this.nextBtn = this.querySelector('[data-vbs-next]');

    if (!this.track || this.slides.length === 0) return;

    this.index = 0;
    this.count = this.slides.length;
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.delay = Math.max(0, Number(this.dataset.autoplay || 0)) * 1000;

    this.#wireControls();
    this.#wireSwipe();
    this.#wirePause();

    this.#goTo(0, { animate: false });
    this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /* -------------------------------------------------------- movement */

  #goTo(i, { animate = true } = {}) {
    // Wrap in both directions so the rail is endless.
    const next = ((i % this.count) + this.count) % this.count;
    const wrapping = Math.abs(next - this.index) > 1;
    const instant = !animate || this.motion.matches || wrapping;

    this.index = next;
    this.track.style.transitionDuration = instant ? '0ms' : '';
    this.track.style.transform = `translate3d(${-100 * next}%, 0, 0)`;

    // Let the browser apply the jump before restoring the transition,
    // otherwise the next move inherits 0ms.
    if (instant && animate) {
      window.setTimeout(() => {
        this.track.style.transitionDuration = '';
      }, 30);
    }

    this.#renderState();
  }

  #renderState() {
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      // Keep offscreen slides out of the tab order.
      slide.querySelectorAll('a, button').forEach((el) => {
        if (active) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  /* -------------------------------------------------------- autoplay */

  #start() {
    this.#stop();
    if (this.delay <= 0 || this.count < 2 || this.motion.matches) return;
    this.timer = window.setInterval(() => this.#goTo(this.index + 1), this.delay);
  }

  #stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  #wirePause() {
    const pause = () => this.#stop();
    const resume = () => this.#start();

    this.addEventListener('mouseenter', pause);
    this.addEventListener('mouseleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);

    // Don't burn cycles advancing a hero nobody is looking at.
    this.onVisibility = () => (document.hidden ? this.#stop() : this.#start());
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  /* -------------------------------------------------------- controls */

  #wireControls() {
    this.prevBtn?.addEventListener('click', () => {
      this.#goTo(this.index - 1);
      this.#start();
    });

    this.nextBtn?.addEventListener('click', () => {
      this.#goTo(this.index + 1);
      this.#start();
    });

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.#goTo(i);
        this.#start();
      });
    });

    this.viewport?.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.#goTo(this.index + 1);
        this.#start();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.#goTo(this.index - 1);
        this.#start();
      }
    });
  }

  /* ----------------------------------------------------------- swipe */

  #wireSwipe() {
    let down = false;
    let startX = 0;
    let dx = 0;

    this.viewport?.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        down = true;
        dx = 0;
        startX = e.clientX;
        this.#stop();
        this.track.style.transitionDuration = '0ms';
      },
      { passive: true }
    );

    this.onMove = (e) => {
      if (!down) return;
      dx = e.clientX - startX;
      const pct = (dx / this.viewport.clientWidth) * 100;
      this.track.style.transform = `translate3d(${-100 * this.index + pct}%, 0, 0)`;
    };

    this.onUp = () => {
      if (!down) return;
      down = false;
      this.track.style.transitionDuration = '';

      // A short flick shouldn't change slides; 40px is about a thumb twitch.
      if (Math.abs(dx) > 40) {
        this.#goTo(this.index + (dx < 0 ? 1 : -1));
        if (Math.abs(dx) > 5) {
          this.viewport.addEventListener('click', (e) => e.preventDefault(), {
            capture: true,
            once: true,
          });
        }
      } else {
        this.#goTo(this.index);
      }

      this.#start();
    };

    window.addEventListener('pointermove', this.onMove, { passive: true });
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }
}

if (!customElements.get('null-slideshow')) {
  customElements.define('null-slideshow', NullSlideshow);
}
